import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import HrDashboardLayout from '../components/layouts/HrDashboardLayout';
import Icon from '../components/atoms/Icon';
import Button from '../components/atoms/Button';
import SessionManager from '../utils/sessionManager';
import { getDocumentVerificationRequests, getDocumentVerificationDocuments, verifyDocument, viewDocument } from '../services/api';

const HrDocumentVerificationPage = () => {
  const navigate = useNavigate();
  const [requests, setRequests] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('all');
  const [selectedRequest, setSelectedRequest] = useState(null);
  const [documents, setDocuments] = useState([]);
  const [documentsLoading, setDocumentsLoading] = useState(false);
  const [viewingDocument, setViewingDocument] = useState(null);
  const [verificationModal, setVerificationModal] = useState(null);
  const [verificationForm, setVerificationForm] = useState({
    status: 'approved',
    notes: ''
  });
  const [verificationLoading, setVerificationLoading] = useState(false);

  // Check HR authentication
  useEffect(() => {
    const isLoggedInAsHr = SessionManager.isLoggedIn('hr');
    const currentSession = SessionManager.getCurrentSession();
    
    if (!isLoggedInAsHr || (currentSession && currentSession.role !== 'hr')) {
      navigate('/hr_login');
      return;
    }
    
    loadRequests();
  }, [navigate]);

  const loadRequests = async () => {
    try {
      setLoading(true);
      const data = await getDocumentVerificationRequests();
      setRequests(data);
    } catch (error) {
      console.error('Error loading verification requests:', error);
    } finally {
      setLoading(false);
    }
  };

  const loadDocuments = async (requestId) => {
    try {
      setDocumentsLoading(true);
      const data = await getDocumentVerificationDocuments(requestId);
      setDocuments(data);
    } catch (error) {
      console.error('Error loading documents:', error);
    } finally {
      setDocumentsLoading(false);
    }
  };

  const handleViewRequest = async (request) => {
    setSelectedRequest(request);
    await loadDocuments(request.request_id);
  };

  const handleViewDocument = async (document) => {
    try {
      const data = await viewDocument(document.document_id);
      setViewingDocument({
        ...document,
        document_data: data.document_data,
        ai_verification_result: data.ai_verification_result
      });
    } catch (error) {
      console.error('Error viewing document:', error);
      alert('Failed to load document');
    }
  };

  const handleVerifyDocument = async () => {
    try {
      setVerificationLoading(true);
      
      await verifyDocument(
        verificationModal.document_id,
        verificationForm.status,
        verificationForm.notes
        // Removed verifiedBy parameter - backend now gets this from JWT token
      );
      
      setVerificationModal(null);
      setVerificationForm({ status: 'approved', notes: '' });
      
      // Reload documents and requests
      await loadDocuments(selectedRequest.request_id);
      await loadRequests();
      
      alert('Document verification updated successfully!');
    } catch (error) {
      console.error('Error verifying document:', error);
      alert('Failed to update document verification');
    } finally {
      setVerificationLoading(false);
    }
  };

  const statusFilters = [
    { value: 'all', label: 'All Requests', color: 'bg-gray-100 text-gray-700' },
    { value: 'pending', label: 'Pending', color: 'bg-gray-200 text-gray-800' },
    { value: 'submitted', label: 'Submitted', color: 'bg-gray-300 text-gray-900' },
    { value: 'verified', label: 'Verified', color: 'bg-green-100 text-green-700' },
    { value: 'rejected', label: 'Rejected', color: 'bg-red-100 text-red-700' }
  ];

  const filteredRequests = filter === 'all' 
    ? requests 
    : requests.filter(req => req.status === filter);

  const getStatusColor = (status) => {
    const colors = {
      pending: 'bg-gray-200 text-gray-800',
      submitted: 'bg-gray-300 text-gray-900',
      verified: 'bg-green-100 text-green-700',
      rejected: 'bg-red-100 text-red-700'
    };
    return colors[status] || 'bg-gray-100 text-gray-700';
  };

  const getVerificationStatusColor = (status) => {
    const colors = {
      pending: 'bg-gray-200 text-gray-800',
      approved: 'bg-green-100 text-green-700',
      rejected: 'bg-red-100 text-red-700'
    };
    return colors[status] || 'bg-gray-100 text-gray-700';
  };

  const getAIRecommendationColor = (recommendation) => {
    const colors = {
      'Genuine': 'bg-green-100 text-green-700',
      'Suspicious': 'bg-gray-300 text-gray-900',
      'Fake': 'bg-red-100 text-red-700'
    };
    return colors[recommendation] || 'bg-gray-100 text-gray-700';
  };

  const documentTypeLabels = {
    degree_certificate: 'Degree Certificate',
    '10th_certificate': '10th Certificate',
    '12th_certificate': '12th Certificate',
    experience_letter: 'Experience Letter',
    relieving_letter: 'Relieving Letter',
    offer_letter: 'Offer Letter',
    pay_slip: 'Pay Slip',
    bank_account: 'Bank Account'
  };

  return (
    <HrDashboardLayout>
      <div className="p-6">
        <div className="flex items-center justify-between mb-6">
          <div>
            <h1 className="text-2xl font-bold text-gray-900">Document Verification</h1>
            <p className="text-gray-600">Manage candidate document verification requests</p>
          </div>
          
          {selectedRequest && (
            <Button
              onClick={() => {
                setSelectedRequest(null);
                setDocuments([]);
              }}
              variant="outline"
            >
              <Icon name="arrow_back" size={20} />
              Back to Requests
            </Button>
          )}
        </div>

        {!selectedRequest ? (
          // Requests List View
          <>
            {/* Filter Tabs */}
            <div className="bg-white rounded-lg shadow-sm p-4 border border-gray-200 mb-6">
              <div className="flex items-center gap-4">
                {statusFilters.map((statusFilter) => (
                  <button
                    key={statusFilter.value}
                    onClick={() => setFilter(statusFilter.value)}
                    className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
                      filter === statusFilter.value
                        ? 'bg-gray-900 text-white'
                        : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                    }`}
                  >
                    {statusFilter.label} ({statusFilter.value === 'all' ? requests.length : requests.filter(r => r.status === statusFilter.value).length})
                  </button>
                ))}
              </div>
            </div>

            {/* Requests Table */}
            <div className="bg-white rounded-lg shadow-sm border border-gray-200">
              {loading ? (
                <div className="p-8 text-center">
                  <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mx-auto"></div>
                  <p className="text-gray-600 mt-2">Loading verification requests...</p>
                </div>
              ) : filteredRequests.length === 0 ? (
                <div className="p-8 text-center">
                  <Icon name="description" size={48} className="text-gray-400 mx-auto mb-4" />
                  <h3 className="text-lg font-medium text-gray-900 mb-2">No verification requests</h3>
                  <p className="text-gray-600">No document verification requests found for the selected filter.</p>
                </div>
              ) : (
                <div className="overflow-x-auto">
                  <table className="min-w-full divide-y divide-gray-200">
                    <thead className="bg-gray-50">
                      <tr>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          Candidate
                        </th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          Request Date
                        </th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          Status
                        </th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          Documents
                        </th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          Progress
                        </th>
                        <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                          Actions
                        </th>
                      </tr>
                    </thead>
                    <tbody className="bg-white divide-y divide-gray-200">
                      {filteredRequests.map((request) => (
                        <tr key={request.request_id} className="hover:bg-gray-50">
                          <td className="px-6 py-4 whitespace-nowrap">
                            <div>
                              <div className="text-sm font-medium text-gray-900">
                                {request.candidate_name}
                              </div>
                              <div className="text-sm text-gray-500">
                                {request.candidate_email}
                              </div>
                            </div>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                            {new Date(request.request_date).toLocaleDateString()}
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap">
                            <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${getStatusColor(request.status)}`}>
                              {request.status}
                            </span>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                            {request.documents_count} uploaded
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap">
                            <div className="flex items-center gap-2">
                              <div className="flex-1 bg-gray-200 rounded-full h-2">
                                <div 
                                  className="bg-gray-900 h-2 rounded-full" 
                                  style={{ width: `${(request.verified_count / Math.max(request.documents_count, 1)) * 100}%` }}
                                ></div>
                              </div>
                              <span className="text-xs text-gray-600">
                                {request.verified_count}/{request.documents_count}
                              </span>
                            </div>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                            <Button
                              onClick={() => handleViewRequest(request)}
                              variant="outline"
                              size="sm"
                            >
                              View Documents
                            </Button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          </>
        ) : (
          // Documents Detail View
          <div className="space-y-6">
            {/* Request Header */}
            <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
              <div className="flex items-center justify-between">
                <div>
                  <h2 className="text-xl font-semibold text-gray-900">
                    {selectedRequest.candidate_name}
                  </h2>
                  <p className="text-gray-600">{selectedRequest.candidate_email}</p>
                  <div className="flex items-center gap-4 mt-2">
                    <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${getStatusColor(selectedRequest.status)}`}>
                      {selectedRequest.status}
                    </span>
                    <span className="text-sm text-gray-600">
                      Requested on {new Date(selectedRequest.request_date).toLocaleDateString()}
                    </span>
                  </div>
                </div>
                <div className="text-right">
                  <div className="text-2xl font-bold text-gray-900">
                    {selectedRequest.verified_count}/{selectedRequest.documents_count}
                  </div>
                  <div className="text-sm text-gray-600">Documents Verified</div>
                </div>
              </div>
            </div>

            {/* Documents Grid */}
            {documentsLoading ? (
              <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-8 text-center">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mx-auto"></div>
                <p className="text-gray-600 mt-2">Loading documents...</p>
              </div>
            ) : (
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                {documents.map((document) => (
                  <div key={document.document_id} className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
                    <div className="flex items-center justify-between mb-4">
                      <h3 className="text-lg font-medium text-gray-900">
                        {documentTypeLabels[document.document_type] || document.document_type}
                      </h3>
                      <Icon name="description" size={24} className="text-gray-400" />
                    </div>
                    
                    <div className="space-y-3">
                      <div>
                        <span className="text-sm text-gray-600">File: </span>
                        <span className="text-sm font-medium">{document.document_name}</span>
                      </div>
                      
                      {document.ai_recommendation && (
                        <div>
                          <span className="text-sm text-gray-600">AI Analysis: </span>
                          <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${getAIRecommendationColor(document.ai_recommendation)}`}>
                            {document.ai_recommendation} ({document.ai_confidence_score}%)
                          </span>
                        </div>
                      )}
                      
                      <div>
                        <span className="text-sm text-gray-600">Status: </span>
                        <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${getVerificationStatusColor(document.manual_verification_status)}`}>
                          {document.manual_verification_status}
                        </span>
                      </div>
                      
                      {document.manual_verified_at && (
                        <div>
                          <span className="text-sm text-gray-600">Verified: </span>
                          <span className="text-sm">{new Date(document.manual_verified_at).toLocaleDateString()}</span>
                        </div>
                      )}
                    </div>
                    
                    <div className="flex gap-2 mt-4">
                      <Button
                        onClick={() => handleViewDocument(document)}
                        variant="outline"
                        size="sm"
                        className="flex-1"
                      >
                        <Icon name="visibility" size={16} />
                        View
                      </Button>
                      
                      {document.manual_verification_status === 'pending' && (
                        <Button
                          onClick={() => setVerificationModal(document)}
                          size="sm"
                          className="flex-1"
                        >
                          <Icon name="check" size={16} />
                          Verify
                        </Button>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}

        {/* Document Viewer Modal */}
        {viewingDocument && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
            <div className="bg-white rounded-lg max-w-4xl max-h-[90vh] overflow-hidden">
              <div className="flex items-center justify-between p-4 border-b">
                <h3 className="text-lg font-medium">
                  {documentTypeLabels[viewingDocument.document_type] || viewingDocument.document_type}
                </h3>
                <button
                  onClick={() => setViewingDocument(null)}
                  className="p-2 rounded-lg hover:bg-gray-100"
                >
                  <Icon name="close" size={24} />
                </button>
              </div>
              
              <div className="p-4 max-h-[70vh] overflow-auto">
                {viewingDocument.file_type === 'pdf' ? (
                  <embed
                    src={`data:application/pdf;base64,${viewingDocument.document_data}`}
                    type="application/pdf"
                    width="100%"
                    height="600px"
                  />
                ) : (
                  <img
                    src={`data:image/${viewingDocument.file_type};base64,${viewingDocument.document_data}`}
                    alt={viewingDocument.document_name}
                    className="max-w-full h-auto"
                  />
                )}
                
                {/* AI Analysis Results */}
                {viewingDocument.ai_verification_result && (
                  <div className="mt-4 p-4 bg-gray-50 rounded-lg">
                    <h4 className="font-medium mb-2">AI Analysis Results</h4>
                    <div className="space-y-2 text-sm">
                      <div>
                        <span className="font-medium">Recommendation: </span>
                        <span className={`px-2 py-1 rounded-full text-xs ${getAIRecommendationColor(viewingDocument.ai_verification_result.recommendation)}`}>
                          {viewingDocument.ai_verification_result.recommendation}
                        </span>
                      </div>
                      <div>
                        <span className="font-medium">Confidence: </span>
                        {viewingDocument.ai_verification_result.confidence_score}%
                      </div>
                      {viewingDocument.ai_verification_result.overall_assessment?.summary && (
                        <div>
                          <span className="font-medium">Summary: </span>
                          {viewingDocument.ai_verification_result.overall_assessment.summary}
                        </div>
                      )}
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>
        )}

        {/* Verification Modal */}
        {verificationModal && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
            <div className="bg-white rounded-lg max-w-md w-full">
              <div className="flex items-center justify-between p-4 border-b">
                <h3 className="text-lg font-medium">Verify Document</h3>
                <button
                  onClick={() => setVerificationModal(null)}
                  className="p-2 rounded-lg hover:bg-gray-100"
                >
                  <Icon name="close" size={24} />
                </button>
              </div>
              
              <div className="p-4">
                <div className="mb-4">
                  <h4 className="font-medium mb-2">
                    {documentTypeLabels[verificationModal.document_type] || verificationModal.document_type}
                  </h4>
                  <p className="text-sm text-gray-600">{verificationModal.document_name}</p>
                </div>
                
                <div className="space-y-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Verification Status
                    </label>
                    <select
                      value={verificationForm.status}
                      onChange={(e) => setVerificationForm({ ...verificationForm, status: e.target.value })}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                    >
                      <option value="approved">Approved</option>
                      <option value="rejected">Rejected</option>
                    </select>
                  </div>
                  
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Notes (Optional)
                    </label>
                    <textarea
                      value={verificationForm.notes}
                      onChange={(e) => setVerificationForm({ ...verificationForm, notes: e.target.value })}
                      rows={3}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                      placeholder="Add any notes about the verification..."
                    />
                  </div>
                </div>
                
                <div className="flex gap-3 mt-6">
                  <Button
                    onClick={handleVerifyDocument}
                    disabled={verificationLoading}
                    className="flex-1"
                  >
                    {verificationLoading ? 'Saving...' : 'Save Verification'}
                  </Button>
                  <Button
                    onClick={() => setVerificationModal(null)}
                    variant="outline"
                    className="flex-1"
                  >
                    Cancel
                  </Button>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    </HrDashboardLayout>
  );
};

export default HrDocumentVerificationPage;
